// src/components/admin/questions/StatsTab.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Search, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import { questionService } from '../../../services/questionService';
import QuestionFilters from '../../question/QuestionFilters';
import { QuestionFilters as Filters } from '../../../types/question.types';
import './StatsTab.css';

interface StatsTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    onEditQuestion: (id: string) => void;
}

interface QuestionStat {
    _id: string;
    statement: string;
    correctAlternative: 'A' | 'B' | 'C' | 'D' | 'E';
    difficulty: string;
    category: {
        level1?: string;
        level2?: string;
        level3?: string;
        level4?: string;
    };
    totalAnswers: number;
    percentages: {
        A: number;
        B: number;
        C: number;
        D: number;
        E: number;
    };
}

const LETTERS: Array<'A' | 'B' | 'C' | 'D' | 'E'> = ['A', 'B', 'C', 'D', 'E'];

const DIFF_LABEL: Record<string, string> = {
    facil: 'Fácil',
    medio: 'Médio',
    dificil: 'Difícil'
};

function stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
}

const StatsTab: React.FC<StatsTabProps> = ({ onShowToast, onEditQuestion }) => {
    const [questions, setQuestions] = useState<QuestionStat[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [advancedFilters, setAdvancedFilters] = useState<Filters>({});
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState<number | undefined>(undefined);

    const loadStats = useCallback(async () => {
        try {
            setLoading(true);
            const data = await questionService.getQuestionsStatsOverview({
                search,
                page,
                limit: 30,
                ...advancedFilters
            });
            setQuestions(data.questions);
            setTotalPages(data.pagination.pages);
            setTotalCount(data.pagination.total);
        } catch {
            onShowToast('Erro ao carregar estatísticas', 'error');
        } finally {
            setLoading(false);
        }
    }, [search, page, advancedFilters, onShowToast]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const handleFiltersChange = (filters: Filters) => {
        setPage(1);
        setAdvancedFilters(filters);
    };

    const handleSearch = (value: string) => {
        setPage(1);
        setSearch(value);
    };

    return (
        <div className="stab-wrapper">

            <QuestionFilters
                onFiltersChange={handleFiltersChange}
                totalQuestions={totalCount}
                loading={loading}
            />

            <div className="stab-search-row">
                <div className="stab-search-box">
                    <Search size={15} />
                    <input
                        type="text"
                        placeholder="Buscar questões..."
                        value={search}
                        onChange={function (e) { handleSearch(e.target.value); }}
                    />
                </div>
            </div>

            {loading ? (
                <div className="stab-loading">
                    <div className="stab-spinner" />
                    <p>Carregando estatísticas...</p>
                </div>
            ) : questions.length === 0 ? (
                <div className="stab-empty">
                    <p>Nenhuma questão encontrada</p>
                </div>
            ) : (
                <>
                    <div className="stab-table-wrap">
                        <table className="stab-table">
                            <thead>
                                <tr>
                                    <th className="stab-th stab-th--statement">Questão</th>
                                    {LETTERS.map(function (l) {
                                        return <th key={l} className="stab-th stab-th--alt">{l}</th>;
                                    })}
                                    <th className="stab-th stab-th--total">Total</th>
                                    <th className="stab-th stab-th--diff">Dificuldade</th>
                                    <th className="stab-th stab-th--actions"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {questions.map(function (q) {
                                    return (
                                        <tr key={q._id} className="stab-tr">
                                            <td className="stab-td stab-td--statement">
                                                <span className="stab-category">
                                                    {q.category?.level1 || '—'}
                                                </span>
                                                <span className="stab-statement">
                                                    {stripHtml(q.statement).substring(0, 120)}...
                                                </span>
                                            </td>
                                            {LETTERS.map(function (letter) {
                                                const pct = q.percentages[letter] || 0;
                                                const isCorrect = letter === q.correctAlternative;
                                                return (
                                                    <td
                                                        key={letter}
                                                        className={
                                                            'stab-td stab-td--alt' +
                                                            (isCorrect ? ' stab-td--correct' : '')
                                                        }
                                                    >
                                                        <span className={
                                                            'stab-pct' +
                                                            (isCorrect ? ' stab-pct--correct' : '')
                                                        }>
                                                            {pct}%
                                                        </span>
                                                        <div className="stab-bar-track">
                                                            <div
                                                                className={
                                                                    'stab-bar-fill' +
                                                                    (isCorrect ? ' stab-bar-fill--correct' : '')
                                                                }
                                                                style={{ width: pct + '%' }}
                                                            />
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            <td className="stab-td stab-td--total">
                                                {q.totalAnswers}
                                            </td>
                                            <td className="stab-td stab-td--diff">
                                                <span className={'stab-diff stab-diff--' + q.difficulty}>
                                                    {DIFF_LABEL[q.difficulty] || '—'}
                                                </span>
                                            </td>
                                            <td className="stab-td stab-td--actions">
                                                <button
                                                    className="stab-edit-btn"
                                                    onClick={function () { onEditQuestion(q._id); }}
                                                    title="Editar questão"
                                                >
                                                    <Edit size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="stab-pagination">
                            <button
                                className="stab-page-btn"
                                onClick={function () { setPage(function (p) { return p - 1; }); }}
                                disabled={page <= 1}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="stab-page-info">
                                <strong>{page}</strong> de <strong>{totalPages}</strong>
                            </span>
                            <button
                                className="stab-page-btn"
                                onClick={function () { setPage(function (p) { return p + 1; }); }}
                                disabled={page >= totalPages}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </>
            )}

        </div>
    );
};

export default StatsTab;